import { Toaster } from "sonner";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { track } from "@/lib/sentry";
import Login from "@/pages/Login";
import CaregiverHome from "@/pages/CaregiverHome";
import ShiftReport from "@/pages/ShiftReport";
import MyReports from "@/pages/MyReports";
import MySchedule from "@/pages/MySchedule";
import MyHours from "@/pages/MyHours";
import MyProfile from "@/pages/MyProfile";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminReports from "@/pages/AdminReports";
import AdminClients from "@/pages/AdminClients";
import AdminAlerts from "@/pages/AdminAlerts";
import AdminIncidents from "@/pages/AdminIncidents";
import AdminMAR from "@/pages/AdminMAR";
import AdminTeam from "@/pages/AdminTeam";
import AdminCompliance from "@/pages/AdminCompliance";
import AdminSchedule from "@/pages/AdminSchedule";
import AdminBilling from "@/pages/AdminBilling";
import AdminTimecards from "@/pages/AdminTimecards";
import AdminEvvExport from "@/pages/AdminEvvExport";
import FamilyInvoice from "@/pages/FamilyInvoice";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminApplicants from "@/pages/AdminApplicants";
import AdminSettings from "@/pages/AdminSettings";
import AdminBAA from "@/pages/AdminBAA";
import AdminAuditLog from "@/pages/AdminAuditLog";
import LegalPage from "@/pages/LegalPage";
import PublicApply from "@/pages/PublicApply";
import PlatformShell, { ImpersonationBanner } from "@/components/PlatformShell";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformAgencies from "@/pages/platform/PlatformAgencies";
import PlatformAgencyDetail from "@/pages/platform/PlatformAgencyDetail";
import PlatformAuditLog from "@/pages/platform/PlatformAuditLog";
import AgencySignup from "@/pages/AgencySignup";
import AgencySignupConfirm from "@/pages/AgencySignupConfirm";
import MarketingLanding from "@/pages/MarketingLanding";
import PricingPage from "@/pages/PricingPage";
import AcceptInvite from "@/pages/AcceptInvite";
import FamilyPortal from "@/pages/FamilyPortal";
import SharedReport from "@/pages/SharedReport";
import ResetPassword from "@/pages/ResetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import Layout from "@/components/Layout";
import AdminShell from "@/components/AdminShell";
import { registerSW } from "@/lib/pwa";
import { flushQueue } from "@/lib/drafts";

// True when a superadmin should be locked out of the agency-admin UI. We
// allow them in IFF they're currently impersonating an agency (the agency UI
// is the whole point of impersonation).
function _isImpersonating() {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((c) => c.trim().startsWith("hc_impersonating="));
}

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthBootSplash />;
  if (!user) return <Navigate to="/login" replace />;
  // Superadmins live in /platform — keep them out of /admin and /clients
  // unless they're actively impersonating an agency.
  if (user.is_superadmin && !_isImpersonating()) {
    return <Navigate to="/platform" replace />;
  }
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  }
  return children;
}

// Gate for the platform console — superadmin-only. Non-superadmins are NOT
// redirected: PlatformShell renders a clear "access restricted" card instead
// (redirecting caused a confusing flicker for users lacking SUPERADMIN_EMAILS).
function PlatformOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthBootSplash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return <AuthBootSplash />;
  // ANONYMOUS visitors land on the marketing page
  if (!user) return <MarketingLanding />;
  if (user.is_superadmin && !_isImpersonating()) {
    return <Navigate to="/platform" replace />;
  }
  return <Navigate to={user.role === "admin" ? "/admin" : "/clients"} replace />;
}

function AuthBootSplash() {
  // Shown for the ~50-200ms window between mount and /auth/me resolution.
  // Prevents flicker through /login during impersonation exit + page reload.
  return (
    <div className="min-h-screen flex items-center justify-center bg-clinical-bg text-clinical-subtext text-sm" />
  );
}

// Module-level constant so Sonner doesn't re-render on every App render
const TOASTER_OPTIONS = {
  style: {
    fontFamily: "'Work Sans', sans-serif",
    borderRadius: "12px",
  },
};

function RoleShell({ children }) {
  const { user } = useAuth();
  if (user?.role === "admin") return <AdminShell>{children}</AdminShell>;
  return <Layout>{children}</Layout>;
}

// Emits a $pageview to PostHog on every route change. Strips query params
// + ids out of the path so we don't leak PHI (client IDs, share tokens).
function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    // Strip dynamic IDs (uuids, share tokens) so PostHog groups paths cleanly.
    const path = location.pathname
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/:id")
      .replace(/\/[0-9a-f]{32}/gi, "/:token")
      .replace(/\/[0-9a-f]{24}/gi, "/:id");
    track("$pageview", { $current_url: window.location.origin + path, path });
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    registerSW();
    const onOnline = () => { flushQueue().catch(() => {}); };
    window.addEventListener("online", onOnline);
    // Attempt one flush on load as well
    flushQueue().catch(() => {});
    return () => window.removeEventListener("online", onOnline);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteChangeTracker />
        <Toaster
          position="top-center"
          toastOptions={TOASTER_OPTIONS}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/agency/signup" element={<AgencySignup />} />
          <Route path="/agency/signup/confirm/:token" element={<AgencySignupConfirm />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/family/:token" element={<FamilyPortal />} />
          <Route path="/family/invoice/:token" element={<FamilyInvoice />} />
          <Route path="/share/report/:token" element={<SharedReport />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/agency/:slug/apply" element={<PublicApply />} />
          <Route path="/" element={<RoleHome />} />

          {/* Caregiver */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute role="caregiver">
                <Layout><CaregiverHome /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/shift/:clientId"
            element={
              <ProtectedRoute role="caregiver">
                <Layout><ShiftReport /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-reports"
            element={
              <ProtectedRoute role="caregiver">
                <Layout><MyReports /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-schedule"
            element={
              <ProtectedRoute role="caregiver">
                <Layout><MySchedule /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-hours"
            element={
              <ProtectedRoute role="caregiver">
                <Layout><MyHours /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute>
                <RoleShell><MyProfile /></RoleShell>
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminDashboard /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminReports /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminClients /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/alerts"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminAlerts /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/incidents"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminIncidents /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/mar"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminMAR /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/schedule"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminSchedule /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/team"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminTeam /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/team/caregivers"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminTeam /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/team/admins"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminTeam /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/compliance"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminCompliance /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminBilling /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/timecards"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminTimecards /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/evv-export"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminEvvExport /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminAnalytics /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/applicants"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminApplicants /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminSettings /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/baa"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminBAA /></AdminShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <ProtectedRoute role="admin">
                <AdminShell><AdminAuditLog /></AdminShell>
              </ProtectedRoute>
            }
          />

          {/* Public legal documents (no auth required) */}
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          {/* Public marketing surfaces */}
          <Route path="/pricing" element={<PricingPage />} />

          <Route
            path="/platform"
            element={
              <PlatformOnly>
                <PlatformShell />
              </PlatformOnly>
            }
          >
            <Route index element={<PlatformDashboard />} />
            <Route path="agencies" element={<PlatformAgencies />} />
            <Route path="agencies/:id" element={<PlatformAgencyDetail />} />
            <Route path="audit-log" element={<PlatformAuditLog />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ImpersonationBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
